import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import getParsedNumber from '../utils/getParsedNumber';
import { selectFilters } from '../redux/filtersReducer';

import SUVSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SUVSelectedIcon';
import SUVDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SUVDefaultIcon';
import SedanSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SedanSelectedIcon';
import SedanDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SedanDefaultIcon';
import AllroadQuattroSelectedIcon from '../assets/icons/FilterBodyTypeOptions/AllroadQuattroSelectedIcon';
import AllroadQuattroDefaultIcon from '../assets/icons/FilterBodyTypeOptions/AllroadQuattroDefaultIcon';
import CoupeSelectedIcon from '../assets/icons/FilterBodyTypeOptions/CoupeSelectedIcon';
import CoupeDefaultIcon from '../assets/icons/FilterBodyTypeOptions/CoupeDefaultIcon';
import SportbackSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SportbackSelectedIcon';
import SportbackDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SportbackDefaultIcon';
import SpyderSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SpyderSelectedIcon';
import SpyderDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SpyderDefaultIcon';
import RoadsterSelectedIcon from '../assets/icons/FilterBodyTypeOptions/RoadsterSelectedIcon';
import RoadsterDefaultIcon from '../assets/icons/FilterBodyTypeOptions/RoadsterDefaultIcon';
import CabrioletSelectedIcon from '../assets/icons/FilterBodyTypeOptions/CabrioletSelectedIcon';
import CabrioletDefaultIcon from '../assets/icons/FilterBodyTypeOptions/CabrioletDefaultIcon';
import AvantSelectedIcon from '../assets/icons/FilterBodyTypeOptions/AvantSelectedIcon';
import AvantDefaultIcon from '../assets/icons/FilterBodyTypeOptions/AvantDefaultIcon';
import { createYearOptionObject } from './useGetYearOptions';
import { selectAppConfig } from '../redux/appConfigReducer';
import nonAudiBrandsFullList from '../constants/listOfNonAudiBrands.json';
import { NonAudiCountType } from '../types';

interface NonAudiBrandType {
  active: boolean;
  code: keyof typeof nonAudiBrandsFullList;
  count: number;
}

export interface NonAudiModelFullList {
  [key: string]: {
    text: string;
  };
}

const useFilterOptions = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const { availableFilters, temporaryFilters, confirmedFilters, nonAudiCounts } =
    useSelector(selectFilters);
  const { marketType, listOfDealers, isTier3page, filterGroupsToRender } =
    useSelector(selectAppConfig);

  const isUsedVehiclesSelected = temporaryFilters.vehicleType === 'used';
  const hasOtherMakesCount = !!availableFilters?.filterResults?.nonAudiManufactures;

  const { filterResults } = availableFilters;

  // ----------------------------------------------------------------------------

  const [allUsedCount, setAllUsedCount] = useState(0);
  const [allAudiUsedCount, setAllAudiUsedCount] = useState(0);
  const [cpoCount, setCpoCount] = useState(0);
  const [usedCount, setUsedCount] = useState(0);
  const [nonAudiCountT3, setNonAudiCountT3] = useState(0);

  useEffect(() => {
    if (temporaryFilters.nonAudi === 'foreign-brand.yes' && !temporaryFilters.otherMakes?.length) {
      setNonAudiCountT3(availableFilters?.filterResults?.foreign_brand_yes);
    }
  }, [
    availableFilters?.filterResults?.foreign_brand_yes,
    temporaryFilters.nonAudi,
    temporaryFilters.otherMakes?.length,
  ]);

  const isAllActive = !!filterResults?.has_warranties?.all?.active;
  const isCPOActive = !!filterResults?.has_warranties?.yes?.active;
  const isUsedActive = !!filterResults?.has_warranties?.no?.active;

  useEffect(() => {
    if (isTier3page) return;
    if (
      temporaryFilters.usedInventoryType === 'has-warranties.all' &&
      !isCPOActive &&
      !isUsedActive
    ) {
      setAllAudiUsedCount(filterResults?.has_warranties?.all?.count);
      setCpoCount(filterResults?.has_warranties?.yes?.count);
      setUsedCount(filterResults?.has_warranties?.no?.count);
    }
  }, [
    filterResults?.has_warranties?.all?.count,
    filterResults?.has_warranties?.no?.count,
    filterResults?.has_warranties?.yes?.count,
    isCPOActive,
    isTier3page,
    isUsedActive,
    temporaryFilters.usedInventoryType,
  ]);

  useEffect(() => {
    if (!isTier3page) return;
    if (
      (temporaryFilters.usedInventoryType === 'has-warranties.all' ||
        temporaryFilters.usedInventoryType === 'has-warranties.combined') &&
      !isCPOActive &&
      !isUsedActive &&
      filterResults?.foreign_brand_yes &&
      filterResults?.foreign_brand_no
    ) {
      setAllUsedCount(filterResults.foreign_brand_yes + filterResults.foreign_brand_no);
      setAllAudiUsedCount(filterResults?.foreign_brand_no);
      setCpoCount(filterResults?.has_warranties?.yes?.count);
      setUsedCount(filterResults?.has_warranties?.no?.count);
      setNonAudiCountT3(filterResults?.foreign_brand_yes);
    }
  }, [
    filterResults?.foreign_brand_no,
    filterResults?.foreign_brand_yes,
    filterResults?.has_warranties?.no?.count,
    filterResults?.has_warranties?.yes?.count,
    isCPOActive,
    isTier3page,
    isUsedActive,
    temporaryFilters.usedInventoryType,
  ]);

  // useEffect(() => {
  //   if (
  //     temporaryFilters.usedInventoryType === 'has-warranties.all' &&
  //     temporaryFilters.nonAudi === 'foreign-brand.no'
  //   ) {
  //     setCpoCount(filterResults?.has_warranties?.yes.count);
  //     setAllUsedCount(filterResults?.has_warranties?.all.count);
  //     setNonAudiCountT3(availableFilters?.filterResults?.foreign_brand_yes);
  //     return;
  //   }
  //   if (temporaryFilters.usedInventoryType === 'has-warranties.yes') {
  //     setCpoCount(filterResults?.has_warranties?.yes.count);
  //     setAllUsedCount(filterResults?.has_warranties?.all.count);
  //   }
  // }, [
  //   availableFilters?.filterResults?.foreign_brand_yes,
  //   filterResults?.has_warranties?.all.count,
  //   filterResults?.has_warranties?.yes.count,
  //   temporaryFilters.nonAudi,
  //   temporaryFilters.usedInventoryType,
  // ]);

  // ----------------------------------------------------------------------------

  const vehicleTypeOptions = useMemo(
    () => ({
      new: {
        label: t('vlp.filters.filterOptions.vehicleType.new'),
        ariaLabel: t('vlp.filters.filterOptions.vehicleType.new'),
        value: 'new',
      },
      used: {
        label: t('vlp.filters.filterOptions.vehicleType.used'),
        ariaLabel: t('vlp.filters.filterOptions.vehicleType.used'),
        value: 'used',
      },
    }),
    [t],
  );

  // TODO: move to another place
  type usedInventoryTypeOptionsTypes = {
    [key: string]: {
      label: string;
      ariaLabel: string;
      value: string;
      popoverDescription?: string;
      count?: number;
    };
  };

  type listOfRangeFiltersTypes = {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          label: string;
          ariaLabel: string;
          value: number;
        };
      };
    };
  };

  const otherMakesOptions = useMemo(() => {
    if (!nonAudiCounts?.nonAudiManufactures) return null;
    const { nonAudiManufactures } = nonAudiCounts;

    // @ts-ignore
    const updatedNonAudiCount = nonAudiManufactures.reduce((acc: any, item: any) => {
      const { code, active, count } = item;
      const makeCode = code as keyof typeof nonAudiBrandsFullList;
      acc[code] = {
        active,
        code,
        count,
        value: code,
        label: nonAudiBrandsFullList[makeCode]?.text,
        ariaLabel: nonAudiBrandsFullList[makeCode]?.text,
      };
      return acc;
    }, {});
    return updatedNonAudiCount;
  }, [nonAudiCounts]);

  const nonAudiModelsOptions = useMemo(() => {
    if (!nonAudiCounts?.nonAudiModels) return null;
    const { nonAudiModels } = nonAudiCounts;

    const fullListOfNonAudiModels = Object.values(nonAudiBrandsFullList).reduce(
      (acc, { members }) => {
        return { ...acc, ...members };
      },
      {},
    ) as NonAudiModelFullList;

    const filteredList = nonAudiModels.filter((model) => {
      const modelCode = model?.code?.substring(2);
      // Unknown Model Code should not be filtered out
      if (modelCode === 'ZZ') {
        return true;
      }
      return fullListOfNonAudiModels[model.code]?.text;
    });
    // @ts-ignore
    const listOfAvailableNonAudiModels = filteredList.reduce((acc: any, item: any) => {
      const { code, active, count } = item;
      const modelCode = code as keyof typeof fullListOfNonAudiModels;
      const makeCode = code.slice(0, 2) as keyof typeof nonAudiBrandsFullList;
      const makeLabel = nonAudiBrandsFullList[makeCode]?.text;
      const isUnknownModelCode = modelCode?.toString()?.substring(2) === 'ZZ';
      // If Model Code is ZZ then it means it has an unknown model code so we should show "Other as Label"
      const label = isUnknownModelCode
        ? t('vlp.filters.filterOptions.nonAudiModels.other')
        : fullListOfNonAudiModels[modelCode]?.text;
      acc[code] = {
        active,
        code,
        count,
        value: code,
        label,
        ariaLabel: label,
        makeLabel,
        makeCode,
      };
      return acc;
    }, {});
    return listOfAvailableNonAudiModels;
  }, [nonAudiCounts, t]);

  const usedInventoryTypeOptions: usedInventoryTypeOptionsTypes = useMemo(() => {
    const {
      // eslint-disable-next-line
      has_warranties,
    } = filterResults ?? {};

    // eslint-disable-next-line camelcase
    const hasWarrantiesNo = has_warranties?.no?.count;
    // eslint-disable-next-line camelcase
    const hasWarrantiesYes = has_warranties?.yes?.count;
    // eslint-disable-next-line camelcase
    const hasWarrantiesAll = has_warranties?.all?.count;
    const nonAudiCount = availableFilters?.filterResults?.foreign_brand_yes;
    const usedVehiclesTier3Count = availableFilters?.filterResults?.foreign_brand_no;

    const canadaMarketOption = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwned', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.allPreOwnedCAN.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: hasWarrantiesAll,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(hasWarrantiesYes ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.certifiedPlus.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: hasWarrantiesYes,
      },
      no: {
        label: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly', {
          has_warranties_no: getParsedNumber(hasWarrantiesNo ?? 0, lang),
        }),
        value: 'has-warranties.no',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.preOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: hasWarrantiesNo,
      },
    };

    const usMarketOption = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwned', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.allPreOwnedUSA.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: allAudiUsedCount,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(hasWarrantiesYes ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.certifiedPreOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: cpoCount,
      },
      no: {
        label: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly', {
          has_warranties_no: getParsedNumber(hasWarrantiesNo ?? 0, lang),
        }),
        value: 'has-warranties.no',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.preOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: usedCount,
      },
    };

    const usMarketOptionTier3 = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.allPreOwnedUSATier3.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: allAudiUsedCount,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(hasWarrantiesYes ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.certifiedPreOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: cpoCount,
      },
      nonAudi: {
        label: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        ariaLabel: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        value: 'foreign-brand.yes',
        isNonAudiFilter: true,
        count: nonAudiCountT3,
      },
      combined: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwned', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.combined',
        isNonAudiFilter: false,
        count: allUsedCount,
      },
    };

    const caMarketOptionTier3 = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.allPreOwnedCANTier3.popoverDescription',
        ),
        isNonAudiFilter: false,
        // count: allUsedCount,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(hasWarrantiesYes ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.certifiedPreOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        // count: cpoCount,
      },
      nonAudi: {
        label: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        ariaLabel: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        value: 'foreign-brand.yes',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.nonAudi.popoverDescription',
        ),
        isNonAudiFilter: true,
        // count: nonAudiCountT3,
      },
      combined: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwned', {
          hasWarrantiesAll: getParsedNumber(hasWarrantiesAll ?? 0, lang),
        }),
        value: 'has-warranties.combined',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.allPreOwnedUSATier3.popoverDescription',
        ),
        isNonAudiFilter: false,
        // count: allUsedCount,
      },
    };

    if (isTier3page) {
      if (marketType === 'CAN') return caMarketOptionTier3;
      return usMarketOptionTier3;
    }

    return marketType === 'CAN' ? canadaMarketOption : usMarketOption;
  }, [
    filterResults,
    availableFilters?.filterResults?.foreign_brand_yes,
    availableFilters?.filterResults?.foreign_brand_no,
    t,
    lang,
    allAudiUsedCount,
    cpoCount,
    usedCount,
    nonAudiCountT3,
    allUsedCount,
    isTier3page,
    marketType,
  ]);

  const nonAudiOption = useMemo(() => {
    return {
      label: 'non audi',
      ariaLabel: 'non audi',
      value: 'foreign-brand.yes',
    };
  }, []);

  const newInventoryTypeOptions = useMemo(() => {
    // eslint-disable-next-line camelcase
    const {
      available_from_soon: availableFromSoon,
      available_from_immediately: availableFromImmediately,
    } = filterResults ?? {};

    const canadaMarketOption = {
      availableInMonths0: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        value: '0',
      },
      // availableInMonths1: {
      //   label: t('vlp.filters.vehicelStatus.new.availableInMonths1'),
      //   ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths1'),
      //   value: '1',
      // },
      // availableInMonths2: {
      //   label: t('vlp.filters.vehicelStatus.new.availableInMonths2'),
      //   ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths2'),
      //   value: '2',
      // },
      availableInMonths3: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        value: '3',
      },
    };

    const usMarketOption = {
      availableInMonths0: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        value: '0',
      },
      // availableInMonths1: {
      //   label: t('vlp.filters.vehicelStatus.new.availableInMonths1'),
      //   ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths1'),
      //   value: '1',
      // },
      // availableInMonths2: {
      //   label: t('vlp.filters.vehicelStatus.new.availableInMonths2'),
      //   ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths2'),
      //   value: '2',
      // },
      availableInMonths3: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        value: '3',
      },
    };

    return marketType === 'CAN' ? canadaMarketOption : usMarketOption;
  }, [filterResults, t, marketType]);

  const modelFamilyOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('modelFamily');
    const isHidden = temporaryFilters.vehicleType === 'new';
    return {
      etron: {
        label: t('vlp.filters.filterOptions.modelFamily.etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.etron'),
        value: 'etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.etron?.count,
        isFilterTagNotRemovable,
      },
      etrongt: {
        label: t('vlp.filters.filterOptions.modelFamily.etrongt'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.etrongt'),
        value: 'etrongt',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.etrongt?.count,
        isFilterTagNotRemovable,
      },
      rsetrongt: {
        label: t('vlp.filters.filterOptions.modelFamily.rsetrongt'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rsetrongt'),
        value: 'rsetrongt',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.rsetrongt?.count,
        isFilterTagNotRemovable,
      },
      a6etron: {
        label: t('vlp.filters.filterOptions.modelFamily.a6etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a6etron'),
        value: 'a6etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.a6etron?.count,
        isFilterTagNotRemovable,
      },
      s6etron: {
        label: t('vlp.filters.filterOptions.modelFamily.s6etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s6etron'),
        value: 's6etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.s6etron?.count,
        isFilterTagNotRemovable,
      },
      q4: {
        label: t('vlp.filters.filterOptions.modelFamily.q4etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q4etron'),
        value: 'q4',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.q4?.count,
        isFilterTagNotRemovable,
      },
      q6etron: {
        label: t('vlp.filters.filterOptions.modelFamily.q6etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q6etron'),
        value: 'q6etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.q6etron?.count,
        isFilterTagNotRemovable,
      },
      sq6etron: {
        label: t('vlp.filters.filterOptions.modelFamily.sq6etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq6etron'),
        value: 'sq6etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.sq6etron?.count,
        isFilterTagNotRemovable,
      },
      q8etron: {
        label: t('vlp.filters.filterOptions.modelFamily.q8etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q8etron'),
        value: 'q8etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.q8etron?.count,
        isFilterTagNotRemovable,
      },
      sq8etron: {
        label: t('vlp.filters.filterOptions.modelFamily.sq8etron'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq8etron'),
        value: 'sq8etron',
        tabs: ['etron'],
        disabled: !filterResults?.model_range?.sq8etron?.count,
        isFilterTagNotRemovable,
      },
      q3: {
        label: t('vlp.filters.filterOptions.modelFamily.q3'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q3'),
        value: 'q3',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.q3?.count,
        isFilterTagNotRemovable,
      },
      'q5-new': {
        label: t('vlp.filters.filterOptions.modelFamily.q5-new'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q5-new'),
        value: 'q5-new',
        tabs: ['q/sq'],
        // disabled: !filterResults?.model_range?.allnewq5?.count,
        isFilterTagNotRemovable,
      },
      'sq5-new': {
        label: t('vlp.filters.filterOptions.modelFamily.sq5-new'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq5-new'),
        value: 'sq5-new',
        tabs: ['q/sq'],
        // disabled: !filterResults?.model_range?.allnewsq5?.count,
        isFilterTagNotRemovable,
      },
      q5: {
        label: t('vlp.filters.filterOptions.modelFamily.q5'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q5'),
        value: 'q5',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.q5?.count,
        isFilterTagNotRemovable,
      },
      q5tfsie: {
        label: t('vlp.filters.filterOptions.modelFamily.q5tfsie'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q5tfsie'),
        value: 'q5tfsie',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.q5tfsie?.count,
        isFilterTagNotRemovable,
      },
      sq5: {
        label: t('vlp.filters.filterOptions.modelFamily.sq5'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq5'),
        value: 'sq5',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.sq5?.count,
        isFilterTagNotRemovable,
      },
      q7: {
        label: t('vlp.filters.filterOptions.modelFamily.q7'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q7'),
        value: 'q7',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.q7?.count,
        isFilterTagNotRemovable,
      },
      sq7: {
        label: t('vlp.filters.filterOptions.modelFamily.sq7'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq7'),
        value: 'sq7',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.sq7?.count,
        isFilterTagNotRemovable,
      },
      q8: {
        label: t('vlp.filters.filterOptions.modelFamily.q8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.q8'),
        value: 'q8',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.q8?.count,
        isFilterTagNotRemovable,
      },
      sq8: {
        label: t('vlp.filters.filterOptions.modelFamily.sq8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.sq8'),
        value: 'sq8',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.sq8?.count,
        isFilterTagNotRemovable,
      },
      rsq8: {
        label: t('vlp.filters.filterOptions.modelFamily.rsq8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rsq8'),
        value: 'rsq8',
        tabs: ['q/sq'],
        disabled: !filterResults?.model_range?.rsq8?.count,
        isFilterTagNotRemovable,
      },
      a3: {
        label: t('vlp.filters.filterOptions.modelFamily.a3'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a3'),
        value: 'a3',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a3?.count,
        isFilterTagNotRemovable,
      },
      s3: {
        label: t('vlp.filters.filterOptions.modelFamily.s3'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s3'),
        value: 's3',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s3?.count,
        isFilterTagNotRemovable,
      },
      rs3: {
        label: t('vlp.filters.filterOptions.modelFamily.rs3'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rs3'),
        value: 'rs3',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.rs3?.count,
        isFilterTagNotRemovable,
      },
      a4: {
        label: t('vlp.filters.filterOptions.modelFamily.a4'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a4'),
        value: 'a4',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a4?.count,
        isFilterTagNotRemovable,
      },
      s4: {
        label: t('vlp.filters.filterOptions.modelFamily.s4'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s4'),
        value: 's4',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s4?.count,
        isFilterTagNotRemovable,
      },
      'a5-new': {
        label: t('vlp.filters.filterOptions.modelFamily.a5-new'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a5-new'),
        value: 'a5-new',
        tabs: ['a/s'],
        // disabled: !filterResults?.model_range?.allnewa5?.count,
        isFilterTagNotRemovable,
      },
      's5-new': {
        label: t('vlp.filters.filterOptions.modelFamily.s5-new'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s5-new'),
        value: 's5-new',
        tabs: ['a/s'],
        // disabled: !filterResults?.model_range?.allnews5?.count,
        isFilterTagNotRemovable,
      },
      a5: {
        label: t('vlp.filters.filterOptions.modelFamily.a5'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a5'),
        value: 'a5',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a5?.count,
        isFilterTagNotRemovable,
      },
      s5: {
        label: t('vlp.filters.filterOptions.modelFamily.s5'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s5'),
        value: 's5',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s5?.count,
        isFilterTagNotRemovable,
      },
      rs5: {
        label: t('vlp.filters.filterOptions.modelFamily.rs5'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rs5'),
        value: 'rs5',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.rs5?.count,
        isFilterTagNotRemovable,
      },
      a6: {
        label: t('vlp.filters.filterOptions.modelFamily.a6'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a6'),
        value: 'a6',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a6?.count,
        isFilterTagNotRemovable,
      },
      s6: {
        label: t('vlp.filters.filterOptions.modelFamily.s6'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s6'),
        value: 's6',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s6?.count,
        isFilterTagNotRemovable,
      },
      rs6: {
        label: t('vlp.filters.filterOptions.modelFamily.rs6'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rs6'),
        value: 'rs6',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.rs6?.count,
        isFilterTagNotRemovable,
      },
      a7: {
        label: t('vlp.filters.filterOptions.modelFamily.a7'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a7'),
        value: 'a7',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a7?.count,
        isFilterTagNotRemovable,
      },
      s7: {
        label: t('vlp.filters.filterOptions.modelFamily.s7'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s7'),
        value: 's7',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s7?.count,
        isFilterTagNotRemovable,
      },
      rs7: {
        label: t('vlp.filters.filterOptions.modelFamily.rs7'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.rs7'),
        value: 'rs7',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.rs7?.count,
        isFilterTagNotRemovable,
      },
      a8: {
        label: t('vlp.filters.filterOptions.modelFamily.a8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.a8'),
        value: 'a8',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.a8?.count,
        isFilterTagNotRemovable,
      },
      s8: {
        label: t('vlp.filters.filterOptions.modelFamily.s8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.s8'),
        value: 's8',
        tabs: ['a/s'],
        disabled: !filterResults?.model_range?.s8?.count,
        isFilterTagNotRemovable,
      },
      r8: {
        label: t('vlp.filters.filterOptions.modelFamily.r8'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.r8'),
        value: 'r8',
        tabs: ['r/tt'],
        disabled: !filterResults?.model_range?.r8?.count,
        isFilterTagNotRemovable,
      },
      tt: {
        label: t('vlp.filters.filterOptions.modelFamily.tt'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.tt'),
        value: 'tt',
        tabs: ['r/tt'],
        disabled: !filterResults?.model_range?.tt?.count,
        isFilterTagNotRemovable,
        isHidden,
      },
      tts: {
        label: t('vlp.filters.filterOptions.modelFamily.tts'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.tts'),
        value: 'tts',
        tabs: ['r/tt'],
        disabled: !filterResults?.model_range?.tts?.count,
        isFilterTagNotRemovable,
        isHidden,
      },
      ttrs: {
        label: t('vlp.filters.filterOptions.modelFamily.ttrs'),
        ariaLabel: t('vlp.filters.filterOptions.modelFamily.ttrs'),
        value: 'ttrs',
        tabs: ['r/tt'],
        disabled: !filterResults?.model_range?.ttrs?.count,
        isFilterTagNotRemovable,
        isHidden,
      },
    };
  }, [t, filterResults, filterGroupsToRender, isTier3page, temporaryFilters.vehicleType]);

  const bodyTypeOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('bodyType');
    return {
      suv: {
        label: t('vlp.filters.filterOptions.bodyType.suv'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.suv'),
        value: 'suv',
        image: SUVDefaultIcon,
        selectedImage: SUVSelectedIcon,
        disabled: !filterResults?.body_type?.suv?.count,
        isFilterTagNotRemovable,
      },
      limousine: {
        label: t('vlp.filters.filterOptions.bodyType.sedan'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.sedan'),
        value: 'limousine',
        image: SedanDefaultIcon,
        selectedImage: SedanSelectedIcon,
        disabled: !filterResults?.body_type?.limousine?.count,
        isFilterTagNotRemovable,
      },
      allroad_quattro: {
        label: t('vlp.filters.filterOptions.bodyType.allroad'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.allroad'),
        value: 'allroad_quattro',
        image: AllroadQuattroDefaultIcon,
        selectedImage: AllroadQuattroSelectedIcon,
        disabled: !filterResults?.body_type?.allroad_quattro?.count,
        isFilterTagNotRemovable,
      },
      coupe: {
        label:
          marketType === 'CAN'
            ? t('vlp.filters.filterOptions.bodyType.coupeCAN')
            : t('vlp.filters.filterOptions.bodyType.coupeUSA'),
        ariaLabel:
          marketType === 'CAN'
            ? t('vlp.filters.filterOptions.bodyType.coupeCAN')
            : t('vlp.filters.filterOptions.bodyType.coupeUSA'),
        value: 'coupe',
        image: CoupeDefaultIcon,
        selectedImage: CoupeSelectedIcon,
        disabled: !filterResults?.body_type?.coupe?.count,
        isFilterTagNotRemovable,
      },
      sportback: {
        label: t('vlp.filters.filterOptions.bodyType.sportback'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.sportback'),
        value: 'sportback',
        image: SportbackDefaultIcon,
        selectedImage: SportbackSelectedIcon,
        disabled: !filterResults?.body_type?.sportback?.count,
        isFilterTagNotRemovable,
      },
      spyder: {
        label: t('vlp.filters.filterOptions.bodyType.spyder'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.spyder'),
        value: 'spyder',
        image: SpyderDefaultIcon,
        selectedImage: SpyderSelectedIcon,
        disabled: !filterResults?.body_type?.spyder?.count,
        isFilterTagNotRemovable,
      },
      roadster: {
        label: t('vlp.filters.filterOptions.bodyType.roadster'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.roadster'),
        value: 'roadster',
        image: RoadsterDefaultIcon,
        selectedImage: RoadsterSelectedIcon,
        disabled: !filterResults?.body_type?.roadster?.count,
        isFilterTagNotRemovable,
      },
      cabriolet: {
        label: t('vlp.filters.filterOptions.bodyType.cabriolet'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.cabriolet'),
        value: 'cabriolet',
        image: CabrioletDefaultIcon,
        selectedImage: CabrioletSelectedIcon,
        disabled: !filterResults?.body_type?.cabriolet?.count,
        isFilterTagNotRemovable,
      },
      avant: {
        label: t('vlp.filters.filterOptions.bodyType.avant'),
        ariaLabel: t('vlp.filters.filterOptions.bodyType.avant'),
        value: 'avant',
        image: AvantDefaultIcon,
        selectedImage: AvantSelectedIcon,
        disabled: !filterResults?.body_type?.avant?.count,
        isFilterTagNotRemovable,
      },
    };
  }, [t, filterResults, marketType, filterGroupsToRender, isTier3page]);

  const trimLineOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('trimLine');
    const canadaMarketOption = {
      comfort: {
        label: t('vlp.filters.filterOptions.trimLine.komfort'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.komfort'),
        value: 'comfort',
        hint: t('vlp.filters.filterOptions.trimLine.komfortHint'),
        disabled: !filterResults?.trimline?.comfort?.count,
        isFilterTagNotRemovable,
      },
      progressive: {
        label: t('vlp.filters.filterOptions.trimLine.progressive'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.progressive'),
        value: 'progressive',
        hint: t('vlp.filters.filterOptions.trimLine.progressiveHint'),
        disabled: !filterResults?.trimline?.progressive?.count,
        isFilterTagNotRemovable,
      },
      technik: {
        label: t('vlp.filters.filterOptions.trimLine.technik'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.technik'),
        value: 'technik',
        hint: t('vlp.filters.filterOptions.trimLine.technikHint'),
        disabled: !filterResults?.trimline?.technik?.count,
        isFilterTagNotRemovable,
      },
    };

    const usMarketOption = {
      premium: {
        label: t('vlp.filters.filterOptions.trimLine.premium'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.premium'),
        value: 'premium',
        hint: t('vlp.filters.filterOptions.trimLine.premiumHint'),
        disabled: !filterResults?.trimline?.premium?.count,
        isFilterTagNotRemovable,
      },
      premium_plus: {
        label: t('vlp.filters.filterOptions.trimLine.premiumPlus'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.premiumPlus'),
        value: 'premium_plus',
        hint: t('vlp.filters.filterOptions.trimLine.premiumPlusHint'),
        disabled: !filterResults?.trimline?.premium_plus?.count,
        isFilterTagNotRemovable,
      },
      prestige: {
        label: t('vlp.filters.filterOptions.trimLine.prestige'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.prestige'),
        value: 'prestige',
        hint: t('vlp.filters.filterOptions.trimLine.prestigeHint'),
        disabled: !filterResults?.trimline?.prestige?.count,
        isFilterTagNotRemovable,
      },
      other: {
        label: t('vlp.filters.filterOptions.trimLine.other'),
        ariaLabel: t('vlp.filters.filterOptions.trimLine.other'),
        value: 'other',
        // hint: t('vlp.filters.filterOptions.trimLine.otherHint'),
        disabled: true,
        isFilterTagNotRemovable,
      },
    };

    return marketType === 'CAN' ? canadaMarketOption : usMarketOption;
  }, [t, filterResults, marketType, filterGroupsToRender, isTier3page]);

  const gearBoxOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('gearBox');
    return {
      automatic: {
        label: t('vlp.filters.filterOptions.gearBox.automatic'),
        ariaLabel: t('vlp.filters.filterOptions.gearBox.automatic'),
        value: 'automatic',
        isFilterTagNotRemovable,
      },
      manual: {
        label: t('vlp.filters.filterOptions.gearBox.manual'),
        ariaLabel: t('vlp.filters.filterOptions.gearBox.manual'),
        value: 'manual',
        isFilterTagNotRemovable,
      },
    };
  }, [t, filterGroupsToRender, isTier3page]);

  const drivetrainOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('drivetrain');
    return {
      fwd: {
        label: t('vlp.filters.filterOptions.drivetrain.fwd'),
        ariaLabel: t('vlp.filters.filterOptions.drivetrain.fwd'),
        value: 'fwd',
        hint: t('vlp.filters.filterOptions.drivetrain.fwdHint'),
        isFilterTagNotRemovable,
      },
      rwd: {
        label: t('vlp.filters.filterOptions.drivetrain.rwd'),
        ariaLabel: t('vlp.filters.filterOptions.drivetrain.rwd'),
        value: 'rwd',
        hint: t('vlp.filters.filterOptions.drivetrain.rwdHint'),
        isFilterTagNotRemovable,
      },
      quattro: {
        label: t('vlp.filters.filterOptions.drivetrain.quattro'),
        ariaLabel: t('vlp.filters.filterOptions.drivetrain.quattro'),
        value: 'quattro',
        hint: t('vlp.filters.filterOptions.drivetrain.quattroHint'),
        isFilterTagNotRemovable,
      },
    };
  }, [t, filterGroupsToRender, isTier3page]);

  const fuelTypeOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('fuelType');
    return {
      gasoline: {
        label: t('vlp.filters.filterOptions.fuelType.gasoline'),
        ariaLabel: t('vlp.filters.filterOptions.fuelType.gasoline'),
        value: 'gasoline',
        isFilterTagNotRemovable,
      },
      diesel: {
        label: t('vlp.filters.filterOptions.fuelType.diesel'),
        ariaLabel: t('vlp.filters.filterOptions.fuelType.diesel'),
        value: 'diesel',
        isFilterTagNotRemovable,
      },
      gasoline_hybrid: {
        label: t('vlp.filters.filterOptions.fuelType.hybrid'),
        ariaLabel: t('vlp.filters.filterOptions.fuelType.hybrid'),
        value: 'gasoline_hybrid',
        isFilterTagNotRemovable,
      },
      electrical: {
        label: t('vlp.filters.filterOptions.fuelType.electric'),
        ariaLabel: t('vlp.filters.filterOptions.fuelType.electric'),
        value: 'electrical',
        isFilterTagNotRemovable,
      },
    };
  }, [t, filterGroupsToRender, isTier3page]);

  const wheelSizeOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('wheelSize');
    return {
      15: {
        label: t('vlp.filters.filterOptions.wheelSize.15'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.15'),
        value: '15',
        isFilterTagNotRemovable,
      },
      16: {
        label: t('vlp.filters.filterOptions.wheelSize.16'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.16'),
        value: '16',
        isFilterTagNotRemovable,
      },
      17: {
        label: t('vlp.filters.filterOptions.wheelSize.17'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.17'),
        value: '17',
        isFilterTagNotRemovable,
      },
      18: {
        label: t('vlp.filters.filterOptions.wheelSize.18'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.18'),
        value: '18',
        isFilterTagNotRemovable,
      },
      19: {
        label: t('vlp.filters.filterOptions.wheelSize.19'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.19'),
        value: '19',
        isFilterTagNotRemovable,
      },
      20: {
        label: t('vlp.filters.filterOptions.wheelSize.20'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.20'),
        value: '20',
        isFilterTagNotRemovable,
      },
      21: {
        label: t('vlp.filters.filterOptions.wheelSize.21'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.21'),
        value: '21',
        isFilterTagNotRemovable,
      },
      22: {
        label: t('vlp.filters.filterOptions.wheelSize.22'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.22'),
        value: '22',
        isFilterTagNotRemovable,
      },
      23: {
        label: t('vlp.filters.filterOptions.wheelSize.23'),
        ariaLabel: t('vlp.filters.filterOptions.wheelSize.23'),
        value: '23',
        isFilterTagNotRemovable,
      },
    };
  }, [filterGroupsToRender, t, isTier3page]);

  const exteriorColorOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('exteriorColor');
    return {
      black: {
        label: t('vlp.filters.filterOptions.exteriorColor.black'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.black'),
        value: 'black',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.black.png',
        isFilterTagNotRemovable,
      },
      white: {
        label: t('vlp.filters.filterOptions.exteriorColor.white'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.white'),
        value: 'white',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.white.png',
        isFilterTagNotRemovable,
      },
      gray: {
        label: t('vlp.filters.filterOptions.exteriorColor.gray'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.gray'),
        value: 'gray',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.gray.png',
        isFilterTagNotRemovable,
      },
      silver: {
        label: t('vlp.filters.filterOptions.exteriorColor.silver'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.silver'),
        value: 'silver',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.silver.png',
        isFilterTagNotRemovable,
      },
      blue: {
        label: t('vlp.filters.filterOptions.exteriorColor.blue'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.blue'),
        value: 'blue',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.blue.png',
        isFilterTagNotRemovable,
      },
      red: {
        label: t('vlp.filters.filterOptions.exteriorColor.red'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.red'),
        value: 'red',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.red.png',
        isFilterTagNotRemovable,
      },
      green: {
        label: t('vlp.filters.filterOptions.exteriorColor.green'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.green'),
        value: 'green',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.green.png',
        isFilterTagNotRemovable,
      },
      brown: {
        label: t('vlp.filters.filterOptions.exteriorColor.brown'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.brown'),
        value: 'brown',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.brown.png',
        isFilterTagNotRemovable,
      },
      yellow: {
        label: t('vlp.filters.filterOptions.exteriorColor.yellow'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.yellow'),
        value: 'yellow',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.yellow.png',
        isFilterTagNotRemovable,
      },
      beige: {
        label: t('vlp.filters.filterOptions.exteriorColor.beige'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.beige'),
        value: 'beige',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.beige.png',
        isFilterTagNotRemovable,
      },
      orange: {
        label: t('vlp.filters.filterOptions.exteriorColor.orange'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.orange'),
        value: 'orange',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.orange.png',
        isFilterTagNotRemovable,
      },
      default: {
        label: t('vlp.filters.filterOptions.exteriorColor.default'),
        ariaLabel: t('vlp.filters.filterOptions.exteriorColor.default'),
        value: 'default',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.default.png',
        isFilterTagNotRemovable,
      },
    };
  }, [t, filterGroupsToRender, isTier3page]);

  const interiorColorOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('interiorColor');
    return {
      black: {
        label: t('vlp.filters.filterOptions.interiorColor.black'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.black'),
        value: 'black',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.black.png',
        isFilterTagNotRemovable,
      },
      gray: {
        label: t('vlp.filters.filterOptions.interiorColor.gray'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.gray'),
        value: 'gray',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.gray.png',
        isFilterTagNotRemovable,
      },
      white: {
        label: t('vlp.filters.filterOptions.interiorColor.white'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.white'),
        value: 'white',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.white.png',
        isFilterTagNotRemovable,
      },
      red: {
        label: t('vlp.filters.filterOptions.interiorColor.red'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.red'),
        value: 'red',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.red.png',
        isFilterTagNotRemovable,
      },
      brown: {
        label: t('vlp.filters.filterOptions.interiorColor.brown'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.brown'),
        value: 'brown',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.brown.png',
        isFilterTagNotRemovable,
      },
      beige: {
        label: t('vlp.filters.filterOptions.interiorColor.beige'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.beige'),
        value: 'beige',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.beige.png',
        isFilterTagNotRemovable,
      },
      default: {
        label: t('vlp.filters.filterOptions.interiorColor.default'),
        ariaLabel: t('vlp.filters.filterOptions.interiorColor.default'),
        value: 'default',
        image:
          'https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/interior-color.default.png',
        isFilterTagNotRemovable,
      },
    };
  }, [filterGroupsToRender, t, isTier3page]);

  const sortMethods = useMemo(
    () => ({
      ...(isTier3page
        ? {
            'DATE_PREDATEEND:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.byOldestInLot'),
              value: 'DATE_PREDATEEND:ASC',
            },
          }
        : {}),
      'salesModelYear:ASC': {
        label: t('vlp.vehicleResults.sort.filterOptions.yearAscending'),
        value: 'salesModelYear:ASC',
      },
      'salesModelYear:DESC': {
        label: t('vlp.vehicleResults.sort.filterOptions.yearDescending'),
        value: 'salesModelYear:DESC',
      },
      'prices.retail:ASC': {
        label: t('vlp.vehicleResults.sort.filterOptions.priceAscending'),
        value: 'prices.retail:ASC',
      },
      'prices.retail:DESC': {
        label: t('vlp.vehicleResults.sort.filterOptions.priceDescending'),
        value: 'prices.retail:DESC',
      },
      ...(!isTier3page
        ? {
            'byDistance:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.distanceAscending'),
              value: 'byDistance:ASC',
            },
          }
        : {}),
      ...(!isTier3page
        ? {
            'byDistance:DESC': {
              label: t('vlp.vehicleResults.sort.filterOptions.distanceDescending'),
              value: 'byDistance:DESC',
            },
          }
        : {}),
      ...(isUsedVehiclesSelected
        ? {
            'mileage:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.mileageAscending'),
              value: 'mileage:ASC',
            },
          }
        : {}),
      ...(isUsedVehiclesSelected
        ? {
            'mileage:DESC': {
              label: t('vlp.vehicleResults.sort.filterOptions.mileageDescending'),
              value: 'mileage:DESC',
            },
          }
        : {}),
    }),
    [isTier3page, isUsedVehiclesSelected, t],
  );

  const powerOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('power');
    return {
      min: {
        0: {
          label: t('vlp.filters.filterOptions.power.0'),
          ariaLabel: t('vlp.filters.filterOptions.power.0'),
          value: 0,
          isFilterTagNotRemovable,
        },
        200: {
          label: t('vlp.filters.filterOptions.power.200'),
          ariaLabel: t('vlp.filters.filterOptions.power.200'),
          value: 200,
          isFilterTagNotRemovable,
        },
        250: {
          label: t('vlp.filters.filterOptions.power.250'),
          ariaLabel: t('vlp.filters.filterOptions.power.250'),
          value: 250,
          isFilterTagNotRemovable,
        },
        300: {
          label: t('vlp.filters.filterOptions.power.300'),
          ariaLabel: t('vlp.filters.filterOptions.power.300'),
          value: 300,
          isFilterTagNotRemovable,
        },
        350: {
          label: t('vlp.filters.filterOptions.power.350'),
          ariaLabel: t('vlp.filters.filterOptions.power.350'),
          value: 350,
          isFilterTagNotRemovable,
        },
        400: {
          label: t('vlp.filters.filterOptions.power.400'),
          ariaLabel: t('vlp.filters.filterOptions.power.400'),
          value: 400,
          isFilterTagNotRemovable,
        },
        500: {
          label: t('vlp.filters.filterOptions.power.500'),
          ariaLabel: t('vlp.filters.filterOptions.power.500'),
          value: 500,
          isFilterTagNotRemovable,
        },
        600: {
          label: t('vlp.filters.filterOptions.power.600'),
          ariaLabel: t('vlp.filters.filterOptions.power.600'),
          value: 600,
          isFilterTagNotRemovable,
        },
      },
      max: {
        200: {
          label: t('vlp.filters.filterOptions.power.200'),
          ariaLabel: t('vlp.filters.filterOptions.power.200'),
          value: 200,
          isFilterTagNotRemovable,
        },
        250: {
          label: t('vlp.filters.filterOptions.power.250'),
          ariaLabel: t('vlp.filters.filterOptions.power.250'),
          value: 250,
          isFilterTagNotRemovable,
        },
        300: {
          label: t('vlp.filters.filterOptions.power.300'),
          ariaLabel: t('vlp.filters.filterOptions.power.300'),
          value: 300,
          isFilterTagNotRemovable,
        },
        350: {
          label: t('vlp.filters.filterOptions.power.350'),
          ariaLabel: t('vlp.filters.filterOptions.power.350'),
          value: 350,
          isFilterTagNotRemovable,
        },
        400: {
          label: t('vlp.filters.filterOptions.power.400'),
          ariaLabel: t('vlp.filters.filterOptions.power.400'),
          value: 400,
          isFilterTagNotRemovable,
        },
        500: {
          label: t('vlp.filters.filterOptions.power.500'),
          ariaLabel: t('vlp.filters.filterOptions.power.500'),
          value: 500,
          isFilterTagNotRemovable,
        },
        600: {
          label: t('vlp.filters.filterOptions.power.600'),
          ariaLabel: t('vlp.filters.filterOptions.power.600'),
          value: 600,
          isFilterTagNotRemovable,
        },
        1000: {
          label: t('vlp.filters.filterOptions.power.600+'),
          ariaLabel: t('vlp.filters.filterOptions.power.600+'),
          value: 1000,
          isFilterTagNotRemovable,
        },
      },
    };
  }, [filterGroupsToRender, t, isTier3page]);

  const mileageOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('mileage');
    return {
      min: {
        0: {
          label: t('vlp.filters.filterOptions.mileage.0'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.0'),
          value: 0,
          isFilterTagNotRemovable,
        },
        25000: {
          label: t('vlp.filters.filterOptions.mileage.25000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.25000'),
          value: 25000,
          isFilterTagNotRemovable,
        },
        50000: {
          label: t('vlp.filters.filterOptions.mileage.50000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.50000'),
          value: 50000,
          isFilterTagNotRemovable,
        },
        75000: {
          label: t('vlp.filters.filterOptions.mileage.75000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.75000'),
          value: 75000,
          isFilterTagNotRemovable,
        },
        100000: {
          label: t('vlp.filters.filterOptions.mileage.100000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.100000'),
          value: 100000,
          isFilterTagNotRemovable,
        },
        125000: {
          label: t('vlp.filters.filterOptions.mileage.125000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.125000'),
          value: 125000,
          isFilterTagNotRemovable,
        },
        150000: {
          label: t('vlp.filters.filterOptions.mileage.150000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.150000'),
          value: 150000,
          isFilterTagNotRemovable,
        },
        175000: {
          label: t('vlp.filters.filterOptions.mileage.175000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.175000'),
          value: 175000,
          isFilterTagNotRemovable,
        },
      },
      max: {
        25000: {
          label: t('vlp.filters.filterOptions.mileage.25000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.25000'),
          value: 25000,
          isFilterTagNotRemovable,
        },
        50000: {
          label: t('vlp.filters.filterOptions.mileage.50000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.50000'),
          value: 50000,
          isFilterTagNotRemovable,
        },
        75000: {
          label: t('vlp.filters.filterOptions.mileage.75000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.75000'),
          value: 75000,
          isFilterTagNotRemovable,
        },
        100000: {
          label: t('vlp.filters.filterOptions.mileage.100000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.100000'),
          value: 100000,
          isFilterTagNotRemovable,
        },
        125000: {
          label: t('vlp.filters.filterOptions.mileage.125000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.125000'),
          value: 125000,
          isFilterTagNotRemovable,
        },
        150000: {
          label: t('vlp.filters.filterOptions.mileage.150000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.150000'),
          value: 150000,
          isFilterTagNotRemovable,
        },
        175000: {
          label: t('vlp.filters.filterOptions.mileage.175000'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.175000'),
          value: 175000,
          isFilterTagNotRemovable,
        },
        500000: {
          label: t('vlp.filters.filterOptions.mileage.200000+'),
          ariaLabel: t('vlp.filters.filterOptions.mileage.200000+'),
          value: 500000,
          isFilterTagNotRemovable,
        },
      },
    };
  }, [filterGroupsToRender, t, isTier3page]);

  const priceOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('price');
    return {
      min: {
        0: {
          label: t('vlp.filters.filterOptions.price.0'),
          ariaLabel: t('vlp.filters.filterOptions.price.0'),
          value: 0,
          isFilterTagNotRemovable,
        },
        10000: {
          label: t('vlp.filters.filterOptions.price.10000'),
          ariaLabel: t('vlp.filters.filterOptions.price.10000'),
          value: 10000,
          isFilterTagNotRemovable,
        },
        20000: {
          label: t('vlp.filters.filterOptions.price.20000'),
          ariaLabel: t('vlp.filters.filterOptions.price.20000'),
          value: 20000,
          isFilterTagNotRemovable,
        },
        30000: {
          label: t('vlp.filters.filterOptions.price.30000'),
          ariaLabel: t('vlp.filters.filterOptions.price.30000'),
          value: 30000,
          isFilterTagNotRemovable,
        },
        40000: {
          label: t('vlp.filters.filterOptions.price.40000'),
          ariaLabel: t('vlp.filters.filterOptions.price.40000'),
          value: 40000,
          isFilterTagNotRemovable,
        },
        50000: {
          label: t('vlp.filters.filterOptions.price.50000'),
          ariaLabel: t('vlp.filters.filterOptions.price.50000'),
          value: 50000,
          isFilterTagNotRemovable,
        },
        60000: {
          label: t('vlp.filters.filterOptions.price.60000'),
          ariaLabel: t('vlp.filters.filterOptions.price.60000'),
          value: 60000,
          isFilterTagNotRemovable,
        },
        70000: {
          label: t('vlp.filters.filterOptions.price.70000'),
          ariaLabel: t('vlp.filters.filterOptions.price.70000'),
          value: 70000,
          isFilterTagNotRemovable,
        },
        80000: {
          label: t('vlp.filters.filterOptions.price.80000'),
          ariaLabel: t('vlp.filters.filterOptions.price.80000'),
          value: 80000,
          isFilterTagNotRemovable,
        },
        90000: {
          label: t('vlp.filters.filterOptions.price.90000'),
          ariaLabel: t('vlp.filters.filterOptions.price.90000'),
          value: 90000,
          isFilterTagNotRemovable,
        },
        100000: {
          label: t('vlp.filters.filterOptions.price.100000'),
          ariaLabel: t('vlp.filters.filterOptions.price.100000'),
          value: 100000,
          isFilterTagNotRemovable,
        },
      },
      max: {
        10000: {
          label: t('vlp.filters.filterOptions.price.10000'),
          ariaLabel: t('vlp.filters.filterOptions.price.10000'),
          value: 10000,
          isFilterTagNotRemovable,
        },
        20000: {
          label: t('vlp.filters.filterOptions.price.20000'),
          ariaLabel: t('vlp.filters.filterOptions.price.20000'),
          value: 20000,
          isFilterTagNotRemovable,
        },
        30000: {
          label: t('vlp.filters.filterOptions.price.30000'),
          ariaLabel: t('vlp.filters.filterOptions.price.30000'),
          value: 30000,
          isFilterTagNotRemovable,
        },
        40000: {
          label: t('vlp.filters.filterOptions.price.40000'),
          ariaLabel: t('vlp.filters.filterOptions.price.40000'),
          value: 40000,
          isFilterTagNotRemovable,
        },
        50000: {
          label: t('vlp.filters.filterOptions.price.50000'),
          ariaLabel: t('vlp.filters.filterOptions.price.50000'),
          value: 50000,
          isFilterTagNotRemovable,
        },
        60000: {
          label: t('vlp.filters.filterOptions.price.60000'),
          ariaLabel: t('vlp.filters.filterOptions.price.60000'),
          value: 60000,
          isFilterTagNotRemovable,
        },
        70000: {
          label: t('vlp.filters.filterOptions.price.70000'),
          ariaLabel: t('vlp.filters.filterOptions.price.70000'),
          value: 70000,
          isFilterTagNotRemovable,
        },
        80000: {
          label: t('vlp.filters.filterOptions.price.80000'),
          ariaLabel: t('vlp.filters.filterOptions.price.80000'),
          value: 80000,
          isFilterTagNotRemovable,
        },
        90000: {
          label: t('vlp.filters.filterOptions.price.90000'),
          ariaLabel: t('vlp.filters.filterOptions.price.90000'),
          value: 90000,
          isFilterTagNotRemovable,
        },
        100000: {
          label: t('vlp.filters.filterOptions.price.100000'),
          ariaLabel: t('vlp.filters.filterOptions.price.100000'),
          value: 100000,
          isFilterTagNotRemovable,
        },
        500000: {
          label: t('vlp.filters.filterOptions.price.100000+'),
          ariaLabel: t('vlp.filters.filterOptions.price.100000+'),
          value: 500000,
          isFilterTagNotRemovable,
        },
      },
    };
  }, [filterGroupsToRender, t, isTier3page]);

  const modelYearOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('modelYear');
    const yearOptions = createYearOptionObject(isFilterTagNotRemovable, isUsedVehiclesSelected);

    return {
      min: yearOptions,
      max: yearOptions,
    };
  }, [filterGroupsToRender, isTier3page, isUsedVehiclesSelected]);

  const listOfRangeFilters: listOfRangeFiltersTypes = {
    power: powerOptions,
    mileage: mileageOptions,
    price: priceOptions,
    year: modelYearOptions,
  };

  const listOfAllFilters = {
    vehicleType: vehicleTypeOptions,
    newInventoryType: newInventoryTypeOptions,
    usedInventoryType: usedInventoryTypeOptions,
    modelFamily: modelFamilyOptions,
    bodyType: bodyTypeOptions,
    trimLine: trimLineOptions,
    gearBox: gearBoxOptions,
    drivetrain: drivetrainOptions,
    fuelType: fuelTypeOptions,
    wheelSize: wheelSizeOptions,
    exteriorColor: exteriorColorOptions,
    interiorColor: interiorColorOptions,
    power: powerOptions,
    mileage: mileageOptions,
    price: priceOptions,
    modelYear: modelYearOptions,
    partnerDealers: listOfDealers,
    nonAudi: nonAudiOption,
    otherMakes: otherMakesOptions,
    nonAudiModels: nonAudiModelsOptions,
  };

  return {
    vehicleTypeOptions,
    usedInventoryTypeOptions,
    newInventoryTypeOptions,
    listOfAllFilters,
    modelFamilyOptions,
    bodyTypeOptions,
    trimLineOptions,
    gearBoxOptions,
    drivetrainOptions,
    fuelTypeOptions,
    wheelSizeOptions,
    exteriorColorOptions,
    interiorColorOptions,
    sortMethods,
    powerOptions,
    mileageOptions,
    priceOptions,
    modelYearOptions,
    listOfRangeFilters,
    nonAudiOption,
    otherMakesOptions,
    nonAudiModelsOptions,
  };
};

export default useFilterOptions;
